import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { api, externalApi } from '../../../../services/authService';
import { selectCurrentToken } from '../../../../redux/authSlice';
import MessageTemplateService from '../../../../services/messageTemplateService';
import moment from 'moment-timezone';
import BasicInfoForm from '../../../../components/campaigns/BasicInfoForm';
import TestCampaignSection from '../../../../components/campaigns/TestCampaignSection';
import CampaignHeader from '../../../../components/campaigns/CampaignHeader';
import CampaignTabs from '../../../../components/campaigns/CampaignTabs';
import ReminderCampaignConfig from '../../../../components/campaigns/reminder/ReminderCampaignConfig';
import CommunicationEndpointsProvider from '../../../../components/campaigns/CommunicationEndpointsProvider';
import { createDefaultReminderCampaign, serializeReminderCampaign } from '../../../../utils/campaignSerializers';
import { normalizeFromEndpoint, getEndpointValueById } from '../../../../utils/endpointUtils';
import ReminderTestConfig from '../../../../components/campaigns/reminder/ReminderTestConfig';
import OptOutConfig from '../../../../components/campaigns/OptOutConfig';
import RetryConfigurationPanel from '../../../../components/campaigns/RetryConfigurationPanel';
import { formatRetryConfigForBackend } from '../../../../utils/retryStrategyUtils';

// Get list of timezones from moment-timezone and format them for display
const timezones = moment.tz.names().map(zone => {
  const offset = moment.tz(zone).format('Z');
  const label = `(UTC${offset}) ${zone.replace(/_/g, ' ')}`;
  return {
    value: zone,
    label
  };
});

const channelTypes = ['email', 'sms', 'voice', 'chat'];

const EditReminderCampaign = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const token = useSelector(selectCurrentToken);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [crmCampaigns, setCrmCampaigns] = useState([]);
  const [statusChoices, setStatusChoices] = useState([]);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [communicationEndpoints, setCommunicationEndpoints] = useState([]);

  // Form state
  const [formData, setFormData] = useState(createDefaultReminderCampaign());

  const [testData, setTestData] = useState({
    email: '',
    phone: '',
    testStatus: null,
    testError: null
  });

  // Load campaign data on component mount
  useEffect(() => {
    const fetchCampaignData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [campaignResponse, accountsResponse, templatesResponse, statusResponse] = await Promise.all([
          api.get(`/acs/nurturing-campaigns/${campaignId}/`),
          api.get('/crm/accounts/'),
          MessageTemplateService.getTemplates({ channel: formData.channel }),
          api.get('/acs/nurturing-campaigns/status_choices/')
        ]);

        const campaign = campaignResponse.data;
        
        // Process the campaign data and migrate voice config if needed
        const processedCampaign = {
          ...campaign,
          // Format dates for the date inputs
          start_date: campaign.start_date ? moment(campaign.start_date).format('YYYY-MM-DD') : null,
          end_date: campaign.end_date ? moment(campaign.end_date).format('YYYY-MM-DD') : null,
          // Process reminder times to include content in the correct place and migrate voice config
          reminder_schedule: {
            ...campaign.reminder_schedule,
            // Preserve the reminder schedule ID if it exists
            id: campaign.reminder_schedule.id,
            reminder_times: campaign.reminder_schedule.reminder_times.map(time => ({
              ...time,
              // Preserve the reminder time ID
              id: time.id,
              messages: time.messages.map(message => {
                const processedMessage = {
                  ...message,
                  // Preserve the message ID
                  id: message.id,
                  // Move content from channel config to message level for non-voice channels
                  content: campaign.channel !== 'voice' ? (message[`${campaign.channel}_config`]?.content || '') : '',
                  // Keep the channel configs with their IDs
                  [`${campaign.channel}_config`]: {
                    ...message[`${campaign.channel}_config`],
                    // Preserve the channel config ID
                    id: message[`${campaign.channel}_config`]?.id
                  }
                };

                // Migrate voice config from old format to new format if needed
                if (campaign.channel === 'voice' && message.voice_config) {
                  const oldVoiceConfig = message.voice_config;
                  processedMessage.voice_config = {
                    // Preserve the voice config ID
                    id: oldVoiceConfig.id,
                    content: oldVoiceConfig.content || '',
                    from_endpoint: oldVoiceConfig.from_endpoint || '',
                    platform: oldVoiceConfig.platform || 'bland_ai',
                    voice_id: oldVoiceConfig.voice_id || oldVoiceConfig.voice || 'alexa',
                    voice_name: oldVoiceConfig.voice_name || 'Alexa (Female)',
                    temperature: oldVoiceConfig.temperature || 0.7,
                    max_duration: oldVoiceConfig.max_duration || 5,
                    retry_attempts: oldVoiceConfig.retry_attempts || 3,
                    voicemail_message: oldVoiceConfig.voicemail_message || '',
                    voicemail_action: oldVoiceConfig.voicemail_action || 'hangup',
                    record_call: oldVoiceConfig.record_call ?? false,
                    call_timeout: oldVoiceConfig.call_timeout || 60,
                    // Migrate platform config with ID preservation
                    platform_config: {
                      id: oldVoiceConfig.platform_config?.id,
                      task: oldVoiceConfig.platform_config?.task || '',
                      pathway_id: oldVoiceConfig.platform_config?.pathway_id || '',
                      pathway_version: oldVoiceConfig.platform_config?.pathway_version || 1,
                      first_sentence: oldVoiceConfig.platform_config?.first_sentence || '',
                      background_track: oldVoiceConfig.platform_config?.background_track || '',
                      model: oldVoiceConfig.platform_config?.model || 'base',
                      wait_for_greeting: oldVoiceConfig.platform_config?.wait_for_greeting ?? false,
                      block_interruptions: oldVoiceConfig.platform_config?.block_interruptions ?? false,
                      interruption_threshold: oldVoiceConfig.platform_config?.interruption_threshold || 100,
                      sensitive_voicemail_detection: oldVoiceConfig.platform_config?.sensitive_voicemail_detection ?? false,
                      transfer_phone_number: oldVoiceConfig.platform_config?.transfer_phone_number || '',
                      transfer_list: oldVoiceConfig.platform_config?.transfer_list || {},
                      language: oldVoiceConfig.platform_config?.language || oldVoiceConfig.language || 'en-US',
                      voicemail_sms: oldVoiceConfig.platform_config?.voicemail_sms || {},
                      dispatch_hours: oldVoiceConfig.platform_config?.dispatch_hours || {},
                      ignore_button_press: oldVoiceConfig.platform_config?.ignore_button_press ?? false,
                      timezone: oldVoiceConfig.platform_config?.timezone || 'America/New_York',
                      request_data: oldVoiceConfig.platform_config?.request_data || {},
                      tools: oldVoiceConfig.platform_config?.tools || [],
                      start_time: oldVoiceConfig.platform_config?.start_time || '',
                      voicemail_action: oldVoiceConfig.platform_config?.voicemail_action || {},
                      retry: oldVoiceConfig.platform_config?.retry || { wait: 300, voicemail_action: 'leave_message' },
                      max_duration: oldVoiceConfig.platform_config?.max_duration || 5,
                      record: oldVoiceConfig.platform_config?.record ?? false,
                      webhook: oldVoiceConfig.platform_config?.webhook || '',
                      webhook_events: oldVoiceConfig.platform_config?.webhook_events || [],
                      metadata: oldVoiceConfig.platform_config?.metadata || {},
                      citation_schema_id: oldVoiceConfig.platform_config?.citation_schema_id || '',
                      analysis_preset: oldVoiceConfig.platform_config?.analysis_preset || 'default',
                      available_tags: oldVoiceConfig.platform_config?.available_tags || [],
                      geospatial_dialing: oldVoiceConfig.platform_config?.geospatial_dialing || 'disabled',
                      precall_dtmf_sequence: oldVoiceConfig.platform_config?.precall_dtmf_sequence || '',
                      dynamic_data: oldVoiceConfig.platform_config?.dynamic_data || [],
                      keywords: oldVoiceConfig.platform_config?.keywords || [],
                      pronunciation_guide: oldVoiceConfig.platform_config?.pronunciation_guide || [],
                      local_dialing: oldVoiceConfig.platform_config?.local_dialing ?? false,
                      // Preserve any existing platform config
                      ...oldVoiceConfig.platform_config
                    },
                    webhook_config: {
                      id: oldVoiceConfig.webhook_config?.id,
                      webhook: oldVoiceConfig.webhook_config?.webhook || '',
                      webhook_events: oldVoiceConfig.webhook_config?.webhook_events || [],
                      ...oldVoiceConfig.webhook_config
                    },
                    metadata: oldVoiceConfig.metadata || {}
                  };
                }

                return processedMessage;
              })
            }))
          }
        };

        setFormData(processedCampaign);
        setAccounts(accountsResponse.data);
        setTemplates(templatesResponse);
        setStatusChoices(statusResponse.data);

        // Load CRM campaigns for the selected account
        if (campaign.account) {
          const crmCampaignsResponse = await api.get(`/crm/accounts/${campaign.account}/campaigns/`);
          setCrmCampaigns(crmCampaignsResponse.data);
        }

        setDataLoaded(true);
      } catch (err) {
        console.error('Error loading campaign data:', err);
        setError('Failed to load campaign data');
      } finally {
        setLoading(false);
      }
    };
    fetchCampaignData();
  }, [campaignId]);

  // Add effect to reload templates when channel changes
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templatesResponse = await MessageTemplateService.getTemplates({ channel: formData.channel });
        setTemplates(templatesResponse);
      } catch (err) {
        console.error('Error loading templates:', err);
      }
    };
    loadTemplates();
  }, [formData.channel]);

  // Fetch CRM campaigns when account changes
  useEffect(() => {
    const fetchCrmCampaigns = async () => {
      if (formData.account) {
        try {
          const response = await api.get(`/crm/accounts/${formData.account}/campaigns/`);
          setCrmCampaigns(response.data);
        } catch (err) {
          setError('Failed to load CRM campaigns');
          console.error('Error loading CRM campaigns:', err);
        }
      } else {
        setCrmCampaigns([]);
      }
    };
    fetchCrmCampaigns();
  }, [formData.account]);

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value,
    }));
  };

  const handleCampaignChange = (updatedCampaign) => {
    // Handle channel switching with voice config preservation
    if (updatedCampaign.channel && updatedCampaign.channel !== formData.channel) {
      // When switching to voice, ensure proper voice config structure
      if (updatedCampaign.channel === 'voice') {
        const updatedSchedule = {
          ...updatedCampaign.reminder_schedule,
          reminder_times: updatedCampaign.reminder_schedule.reminder_times.map(time => ({
            ...time,
            messages: time.messages.map(message => ({
              ...message,
              // Ensure voice config has proper structure
              voice_config: {
                content: '',
                from_endpoint: '',
                platform: 'bland_ai',
                voice_id: 'alexa',
                voice_name: 'Alexa (Female)',
                temperature: 0.7,
                max_duration: 5,
                retry_attempts: 3,
                voicemail_message: '',
                voicemail_action: 'hangup',
                record_call: false,
                call_timeout: 60,
                platform_config: {
                  task: '',
                  pathway_id: '',
                  pathway_version: 1,
                  first_sentence: '',
                  background_track: '',
                  model: 'base',
                  wait_for_greeting: false,
                  block_interruptions: false,
                  interruption_threshold: 100,
                  sensitive_voicemail_detection: false,
                  transfer_phone_number: '',
                  transfer_list: {},
                  language: 'en-US',
                  voicemail_sms: {},
                  dispatch_hours: {},
                  ignore_button_press: false,
                  timezone: 'America/New_York',
                  request_data: {},
                  tools: [],
                  start_time: '',
                  voicemail_action: {},
                  retry: { wait: 300, voicemail_action: 'leave_message' },
                  max_duration: 5,
                  record: false,
                  webhook: '',
                  webhook_events: [],
                  metadata: {},
                  citation_schema_id: '',
                  analysis_preset: 'default',
                  available_tags: [],
                  geospatial_dialing: 'disabled',
                  precall_dtmf_sequence: '',
                  dynamic_data: [],
                  keywords: [],
                  pronunciation_guide: [],
                  local_dialing: false
                },
                webhook_config: {
                  webhook: '',
                  webhook_events: []
                },
                metadata: {}
              }
            }))
          }))
        };
        updatedCampaign.reminder_schedule = updatedSchedule;
      }
    }

    setFormData(prev => ({
      ...prev,
      ...updatedCampaign,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSaveSuccess(false);

    try {
      // Serialize the campaign data
      const serializedCampaign = serializeReminderCampaign(formData);

      // Add properly formatted retry configuration
      const retryConfig = formatRetryConfigForBackend(formData.retry_strategy_id, formData.max_retries);
      const finalPayload = {
        ...serializedCampaign,
        ...retryConfig
      };

      // Update the campaign
      const response = await api.put(`/acs/reminder-campaigns/${campaignId}/`, finalPayload);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Campaign update error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setLoading(true);
    setError(null);

    try {
      await api.post(`/acs/nurturing-campaigns/${campaignId}/update_status/`, {
        status: 'active'
      });
      setActivateDialogOpen(false);
      navigate('/crm/lead-nurturing');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleTestCampaign = async (testData) => {
    setTestData(prev => ({ ...prev, testStatus: 'loading', testError: null }));
    try {
      if (testData.error) {
        throw new Error(testData.error);
      }

      const selectedReminder = formData.reminder_schedule.reminder_times[testData.reminderIndex];
      const message = selectedReminder.messages[0];

      if (formData.channel === 'voice') {
        // Voice-specific test handling
        const voiceConfig = message.voice_config;
        const fromEndpoint = voiceConfig.from_endpoint;

        if (!fromEndpoint) {
          throw new Error('From endpoint is required for voice campaigns');
        }

        // Platform-specific test payload
        const testPayload = {
          phone_number: testData.phone,
          platform: voiceConfig.platform,
          voice_id: voiceConfig.voice_id,
          temperature: voiceConfig.temperature,
          max_duration: voiceConfig.max_duration,
          from_endpoint: fromEndpoint,
          platform_config: voiceConfig.platform_config,
          webhook_config: voiceConfig.webhook_config
        };

        const response = await externalApi.post('/communications/send-direct-voice/', testPayload);
        
        if (response.data.success) {
          setTestData(prev => ({ 
            ...prev, 
            testStatus: 'success', 
            testError: null 
          }));
        } else {
          throw new Error(response.data.detail || 'Failed to send test voice call');
        }
      } else {
        // Handle other channels (SMS, email, chat)
        const message = selectedReminder.messages[0]?.template ? 
          templates.find(t => t.id === selectedReminder.messages[0].template)?.content : 
          selectedReminder.messages[0]?.content;

        // Get the endpoint value for the selected channel
        const fromEndpoint = selectedReminder.messages[0]?.[`${formData.channel}_config`]?.from_endpoint_value;

        if (!message || !fromEndpoint) {
          throw new Error('Message content and from endpoint are required');
        }

        const testPayload = {
          phone_number: testData.phone,
          message: message,
          twilio_number: fromEndpoint
        };
        
        const response = await externalApi.post('/communications/send-direct-sms/', testPayload);
        
        if (response.data.success) {
          setTestData(prev => ({ 
            ...prev, 
            testStatus: 'success', 
            testError: null 
          }));
        } else {
          throw new Error(response.data.detail || 'Failed to send test message');
        }
      }
    } catch (err) {
      console.error('Test Campaign - Error:', err);
      setTestData(prev => ({
        ...prev,
        testStatus: 'error',
        testError: err.response?.data?.detail || err.message || 'Failed to send test message'
      }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderMessageConfig = () => {
    return (
      <CommunicationEndpointsProvider
        accountId={formData.account}
        campaignId={formData.crm_campaign}
        channel={formData.channel}
        activeTab={activeTab}
      >
        {({ endpoints, loading: endpointsLoading, error: endpointsError }) => (
          <Grid container spacing={3}>
            {endpointsError && (
              <Grid item xs={12}>
                <Alert severity="error">{endpointsError}</Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <ReminderCampaignConfig
                campaign={formData}
                onCampaignChange={handleCampaignChange}
                templates={templates}
                communicationEndpoints={endpoints}
                timezones={timezones}
              />
            </Grid>

            <Grid item xs={12}>
              <OptOutConfig
                config={{
                  enable_opt_out: formData.enable_opt_out,
                  initial_opt_out_notice: formData.initial_opt_out_notice,
                  opt_out_message: formData.opt_out_message
                }}
                onChange={(updatedConfig) => {
                  setFormData(prev => ({
                    ...prev,
                    ...updatedConfig
                  }));
                }}
              />
            </Grid>

            {/* Retry Configuration */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mt: 2 }}>
                <RetryConfigurationPanel
                  retryStrategy={formData.retry_strategy_id}
                  maxRetries={formData.max_retries}
                  onRetryStrategyChange={(strategyId) => setFormData(prev => ({ ...prev, retry_strategy_id: strategyId }))}
                  onMaxRetriesChange={(maxRetries) => setFormData(prev => ({ ...prev, max_retries: maxRetries }))}
                  description="Configure retry behavior for all reminder messages (campaign-level default)"
                />
              </Paper>
            </Grid>
          </Grid>
        )}
      </CommunicationEndpointsProvider>
    );
  };

  const renderTestConfig = () => {
    return (
      <ReminderTestConfig
        campaign={formData}
        templates={templates}
        onTest={handleTestCampaign}
        testStatus={testData.testStatus}
        testError={testData.testError}
      />
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <CampaignHeader
          title="Edit Reminder Campaign"
          loading={loading}
          onBack={() => navigate('/crm/lead-nurturing')}
          onSave={handleSubmit}
          onActivate={() => setActivateDialogOpen(true)}
          showActivateButton={true}
          status={formData.status}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Campaign updated successfully!
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={60} />
          </Box>
        ) : dataLoaded ? (
          <>
            <CampaignTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              campaignType="reminder"
              isEdit={true}
            />

            <Box sx={{ mt: 3 }}>
              {activeTab === 0 && (
                <BasicInfoForm
                  formData={formData}
                  onInputChange={handleInputChange}
                  accounts={accounts}
                  crmCampaigns={crmCampaigns}
                  campaignTypes={{ reminder: 'Reminder Campaign' }}
                />
              )}
              {activeTab === 1 && renderMessageConfig()}
              {activeTab === 2 && renderTestConfig()}
            </Box>
          </>
        ) : null}
      </Paper>

      <Dialog
        open={activateDialogOpen}
        onClose={() => setActivateDialogOpen(false)}
      >
        <DialogTitle>Activate Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to activate this campaign? Once activated, the campaign will start sending messages according to the schedule.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleActivate} color="success" variant="contained">
            Activate
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditReminderCampaign; 